import React from "react";
import { CheckCircle2, Globe2, ShieldCheck, Zap } from "lucide-react";
import { SiXero, SiQuickbooks } from "react-icons/si";

const stats = [
  { icon: Globe2, label: "100% Online" },
  { icon: ShieldCheck, label: "MTD Ready" },
  { icon: CheckCircle2, label: "MAAT Qualified" },
  { icon: Zap, label: "Fast Turnarounds" },
];

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative isolate overflow-hidden bg-[#D9EAC1] text-[#2A3430]"
  >
    {/* decorative soft washes */}
    <div className="pointer-events-none absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full bg-[#C57A5A]/10 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-[#EAF4DD] blur-3xl" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-24 md:pb-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div className="lg:col-span-7">
          <span
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full bg-white/70 ring-1 ring-[#2A3430]/10 backdrop-blur px-3.5 py-1.5 text-[12px] tracking-wide uppercase text-[#2A3430]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C57A5A]" />
            Online Accountants
          </span>

          <h1
            data-testid="hero-heading"
            className="font-serif-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2A3430]"
          >
            Smart accounting for <span className="italic text-[#C57A5A]">ambitious</span> entrepreneurs.
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#2A3430]/75">
            We handle your books, tax and compliance — so you can focus on
            growing the business. Cloud-first, MTD-ready and refreshingly
            jargon-free.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#pricing"
              data-testid="hero-cta-pricing"
              className="inline-flex items-center justify-center bg-[#C57A5A] hover:bg-[#A96444] text-white font-medium px-6 py-3.5 rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#C57A5A]/25 hover:-translate-y-0.5"
            >
              See our prices
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center border border-[#2A3430]/25 hover:bg-white/50 text-[#2A3430] font-medium px-6 py-3.5 rounded-full transition-all duration-300"
            >
              Book a free call
            </a>
          </div>

          {/* Software badges */}
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#2A3430]/55">
            <span className="text-xs uppercase tracking-widest">Works with</span>
            <SiXero className="h-6 w-auto" title="Xero" />
            <SiQuickbooks className="h-6 w-auto" title="QuickBooks" />
            <span className="text-sm font-medium tracking-tight">FreeAgent</span>
          </div>
        </div>

        {/* Right — Stats card */}
        <div className="lg:col-span-5">
          <div
            data-testid="hero-stats-card"
            className="relative rounded-3xl bg-white ring-1 ring-[#2A3430]/8 shadow-xl shadow-[#2A3430]/10 p-7 md:p-8"
          >
            <div className="flex items-baseline justify-between mb-6">
              <h3 className="font-serif-display text-xl text-[#2A3430]">
                Why founders choose us
              </h3>
              <span className="text-xs text-[#2A3430]/50">2026</span>
            </div>

            <ul className="grid grid-cols-2 gap-4">
              {stats.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-2xl bg-[#EAF4DD] hover:bg-[#D9EAC1] ring-1 ring-[#2A3430]/8 hover:ring-[#C57A5A]/40 transition p-4"
                >
                  <Icon className="h-5 w-5 text-[#C57A5A]" strokeWidth={1.75} />
                  <p className="mt-3 text-sm font-medium text-[#2A3430]">{label}</p>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-6 border-t border-[#2A3430]/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#2A3430]/50">
                  Trusted by
                </p>
                <p className="text-base font-medium text-[#2A3430] mt-1">
                  Founders, freelancers & SMEs
                </p>
              </div>
              <div className="flex -space-x-2">
                {["AA", "NW", "+"].map((t, i) => (
                  <div
                    key={i}
                    className="h-9 w-9 rounded-full bg-gradient-to-br from-[#C57A5A] to-[#A96444] ring-2 ring-white grid place-items-center text-[11px] font-semibold text-white"
                  >
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
